import * as React from 'react';
import { styled, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Drawer from '@mui/material/Drawer';
import MenuIcon from '@mui/icons-material/Menu';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import Sitemark from './SitemarkIcon';
import ColorModeIconDropdown from '../../shared-theme/ColorModeIconDropdown';
import type { } from '@mui/material/themeCssVarsAugmentation';
import { Link } from 'react-router-dom';
import { useNavigate } from "react-router-dom";
import { AuthData } from '../../../pages/Auth/reducer';
import { HandymanAppState } from '../../../configuration/redux/reducers';
import { connect } from 'react-redux';
import { getCurrentUserDetails, getUserDetails, logout } from '../../../pages/Auth/actions';
import { Avatar, Menu, Typography } from '@mui/material';
import { UserDetailsViewModel } from '../../../configuration/api/apiSchema';
import { getAuthData } from '../../../helpers/localStorageHelpers';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ChatIcon from '@mui/icons-material/Chat';
import Badge from '@mui/material/Badge';
import { List, ListItem, ListItemAvatar, ListItemText } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import TaskIcon from '@mui/icons-material/Task';
import EventIcon from '@mui/icons-material/Event';

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
  borderRadius: `calc(${theme.shape.borderRadius}px + 8px)`,
  backdropFilter: 'blur(24px)',
  border: '1px solid',
  borderColor: (theme.vars || theme).palette.divider,
  backgroundColor: theme.vars
    ? `rgba(${theme.vars.palette.background.defaultChannel} / 0.4)`
    : alpha(theme.palette.background.default, 0.4),
  boxShadow: (theme.vars || theme).shadows[1],
  padding: '8px 12px',
}));

interface DispatchFromProps {
  logout: () => void
  getCurrentUserDetails: () => void
}

interface StateFromProps {
  authData?: AuthData;
  currentUserDetails?: UserDetailsViewModel;
  unreadChatsCount?: number;
}

interface PassedProps {

}

interface NotificationType {
  id: number;
  type: 'message' | 'task' | 'event';
  title: string;
  description: string;
  timestamp: Date;
  read: boolean;
}

const mockNotifications: NotificationType[] = [
  {
    id: 1,
    type: 'message',
    title: 'New Message',
    description: 'Bob sent you a new message',
    timestamp: new Date(Date.now() - 1000 * 60 * 5),
    read: false,
  },
  {
    id: 2,
    type: 'task',
    title: 'Task Update',
    description: 'Your task "Fix the sink" has been accepted',
    timestamp: new Date(Date.now() - 1000 * 60 * 30),
    read: false,
  },
  {
    id: 3,
    type: 'event',
    title: 'New Event',
    description: 'Scheduled maintenance tomorrow at 2 PM',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2),
    read: true,
  },
];

const AppBarComponent = (props: DispatchFromProps & StateFromProps & PassedProps) => {
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);

  const navigate = useNavigate();

  const toggleDrawer = (newOpen: boolean) => () => {
    setIsDrawerOpen(newOpen);
  };

  React.useEffect(() => {
    if (props.authData) {
      props.getCurrentUserDetails()
    }
  }, [props.authData])

  let isHandyman = props.authData?.role === "handyman";
  let isCustomer = props.authData?.role === "customer";
  let isLoggedIn = props.authData;

  const localAuthData = getAuthData();
  if (localAuthData && !props.authData) {
    isHandyman = localAuthData.role === "handyman";
    isCustomer = localAuthData.role === "customer";
    isLoggedIn = localAuthData;
  }

  const username = props.currentUserDetails ? `${props.currentUserDetails?.firstName} ${props.currentUserDetails?.lastName}` : 'Loading...';
  const avatar = props.currentUserDetails ? props.currentUserDetails?.avatarUrl : '';

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isProfileMenuOpen = Boolean(anchorEl);
  const handleProfileMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const [notificationsAnchor, setNotificationsAnchor] = React.useState<null | HTMLElement>(null);
  const unreadNotifications = mockNotifications.filter(n => !n.read).length;

  const handleNotificationsClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationsAnchor(event.currentTarget);
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchor(null);
  };

  return (
    <AppBar
      position="fixed"
      enableColorOnDark
      sx={{
        boxShadow: 0,
        bgcolor: 'transparent',
        backgroundImage: 'none',
        mt: 'calc(var(--template-frame-height, 0px) + 28px)',
      }}
    >
      <Container maxWidth="lg">
        <StyledToolbar variant="dense" disableGutters>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', px: 0 }}>
            <div
              onClick={() => navigate("/")}
              style={{ cursor: "pointer" }}
            >
              <Sitemark />
            </div>

            {isLoggedIn && <Button variant="text" color="info" size="small" onClick={() => navigate("profile")}>
              Profile
            </Button>
            }

            {!isLoggedIn && <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
              <Button variant="text" color="info" size="small">
                Services
              </Button>
              <Button
                variant="text"
                color="info"
                size="small"
                sx={{
                  border: '1px solid',
                  borderColor: 'info.main',
                  borderRadius: '4px',
                }}
              >
                Become a Tasker
              </Button>
            </Box>
            }

            {isCustomer && <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
              <Button variant="text" color="info" size="small" onClick={() => navigate("orders")}>
                Orders
              </Button>
              <Button
                variant="text"
                color="info"
                size="small"
                sx={{
                  border: '1px solid',
                  borderColor: 'info.main',
                  borderRadius: '4px',
                }}
                onClick={() => navigate("browse-handyman")}
              >
                Browse Handyman
              </Button>
            </Box>
            }

            {isHandyman && <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
              <Button
                variant="text"
                color="info"
                size="small"
                sx={{
                  border: '1px solid',
                  borderColor: 'info.main',
                  borderRadius: '4px',
                }}
                onClick={() => navigate("orders")}
              >
                My Tasks
              </Button>
            </Box>
            }

            {isLoggedIn && <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
              <Button variant="text" color="info" size="small" onClick={() => navigate("support")}>
                Help
              </Button>
            </Box>
            }

          </Box>

          {!isLoggedIn && <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Button color="primary" variant="text" size="small" onClick={() => navigate("sign-in")}>
              Sign in
            </Button>
            <Button color="primary" variant="contained" size="small" onClick={() => navigate("sign-up")}>
              Sign up
            </Button>
            <ColorModeIconDropdown />
          </Box>
          }

          {isLoggedIn && (
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                gap: 1,
                alignItems: 'center',
              }}
            >
              {/* Chat and Notifications Icons */}
              <IconButton
                color="info"
                size="small"
                onClick={() => navigate("chats")}
              >
                <Badge badgeContent={props.unreadChatsCount} color="error">
                  <ChatIcon />
                </Badge>
              </IconButton>
              <IconButton
                color="info"
                size="small"
                onClick={handleNotificationsClick}
              >
                <Badge badgeContent={unreadNotifications} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>

              {/* Avatar and Full Name */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Avatar
                  alt={username}
                  src={avatar as string}
                  sx={{ width: 32, height: 32 }}
                  onClick={(e: any) => handleProfileMenuClick(e)}
                />
                <Button color="primary" variant="text" size="small" onClick={handleProfileMenuClick}>
                  {username}
                </Button>
              </Box>
              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={isProfileMenuOpen}
                onClose={handleClose}
                MenuListProps={{
                  'aria-labelledby': 'basic-button',
                }}
              >
                <MenuItem
                  onClick={() => {
                    handleClose();
                    navigate("profile");
                  }}
                >
                  Edit Profile
                </MenuItem>
                <MenuItem onClick={(e: any) => {
                  props.logout()
                  handleClose()
                }}>
                  Logout
                </MenuItem>
              </Menu>

              {/* Color Mode Dropdown */}
              <ColorModeIconDropdown />
            </Box>
          )}

          {/* Phone View */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 1 }}>
            <ColorModeIconDropdown size="medium" />
            <IconButton aria-label="Menu button" onClick={toggleDrawer(true)}>
              <MenuIcon />
            </IconButton>
            <Drawer
              anchor="top"
              open={isDrawerOpen}
              onClose={toggleDrawer(false)}
              PaperProps={{
                sx: {
                  top: 'var(--template-frame-height, 0px)',
                },
              }}
            >
              <Box sx={{ p: 2, backgroundColor: 'background.default' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <IconButton onClick={toggleDrawer(false)}>
                    <CloseRoundedIcon />
                  </IconButton>
                </Box>

                {isLoggedIn && <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1, // Space between avatar and text
                  }}
                >
                  <Avatar
                    alt={username}
                    src={avatar as string}
                    sx={{ width: 32, height: 32 }}
                    onClick={(e: any) => handleProfileMenuClick(e)}
                  />
                  <Button color="primary" variant="text" size="small" onClick={handleProfileMenuClick}>
                    {username}
                  </Button>
                </Box>
                }

                {isLoggedIn && <>
                  <MenuItem onClick={() => navigate("profile")}>Profile</MenuItem>
                </>
                }

                {!isLoggedIn && <>
                  <MenuItem>Services</MenuItem>
                  <MenuItem>Become a Tasker</MenuItem>
                  <Divider sx={{ my: 3 }} />
                  <MenuItem>
                    <Button
                      color="primary"
                      variant="contained"
                      fullWidth
                      onClick={() => navigate("sign-up")}
                    >
                      Sign up
                    </Button>
                  </MenuItem>
                  <MenuItem>
                    <Button
                      color="primary"
                      variant="outlined"
                      fullWidth
                      onClick={() => navigate("sign-in")}
                    >
                      Sign in
                    </Button>
                  </MenuItem>
                </>
                }

                {isCustomer && <>
                  <MenuItem onClick={() => navigate("orders")}>Orders</MenuItem>
                  <MenuItem onClick={() => navigate("browse-handyman")}>Browse Handyman</MenuItem>
                </>
                }
                {isLoggedIn && <>
                  <MenuItem onClick={() => navigate("chats")}>Chats</MenuItem>
                  <MenuItem onClick={() => navigate("support")}>Help</MenuItem>
                </>
                }
                {isHandyman && <>
                  <MenuItem onClick={() => navigate("orders")}>My Tasks</MenuItem>
                </>
                }

                {isLoggedIn && <>
                  <MenuItem>
                    <Button
                      color="primary"
                      variant="contained"
                      fullWidth
                      onClick={() => props.logout()}
                    >
                      Log out
                    </Button>
                  </MenuItem>
                </>
                }
              </Box>
            </Drawer>
          </Box>
        </StyledToolbar>
      </Container>

      <Menu
        anchorEl={notificationsAnchor}
        open={Boolean(notificationsAnchor)}
        onClose={handleNotificationsClose}
        PaperProps={{
          sx: {
            width: 360,
            maxHeight: '80vh',
            overflowY: 'auto',
            mt: 1,
            boxShadow: 3,
            borderRadius: 2,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Notifications</Typography>
          <Button color="primary" size="small" sx={{ textTransform: 'none' }}>
            Mark all as read
          </Button>
        </Box>
        <Divider />
        <List sx={{ p: 0 }}>
          {mockNotifications.map((notification) => (
            <React.Fragment key={notification.id}>
              <ListItem
                alignItems="flex-start"
                sx={{
                  bgcolor: notification.read ? 'transparent' : alpha('#1976d2', 0.04),
                  '&:hover': { bgcolor: alpha('#1976d2', 0.08) },
                  transition: 'background-color 0.2s',
                  cursor: 'pointer',
                }}
              >
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: notification.type === 'message' ? 'primary.main' : notification.type === 'task' ? 'success.main' : 'info.main' }}>
                    {notification.type === 'message' && <ChatIcon />}
                    {notification.type === 'task' && <TaskIcon />}
                    {notification.type === 'event' && <EventIcon />}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography variant="subtitle2" sx={{ fontWeight: notification.read ? 400 : 600 }}>
                      {notification.title}
                    </Typography>
                  }
                  secondary={
                    <React.Fragment>
                      <Typography variant="body2" color="text.primary" sx={{ mt: 0.5, mb: 0.5 }}>
                        {notification.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDistanceToNow(notification.timestamp, { addSuffix: true })}
                      </Typography>
                    </React.Fragment>
                  }
                />
              </ListItem>
              <Divider component="li" />
            </React.Fragment>
          ))}
        </List>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
          <Button 
            color="primary" 
            size="small" 
            sx={{ 
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            View All Notifications
          </Button>
        </Box>
      </Menu>
    </AppBar>
  );
}

const mapStateToProps = (state: HandymanAppState): StateFromProps => {
  return {
    authData: state.user.authData,
    currentUserDetails: state.user.currentUserDetails
  };
};

export default connect<
  StateFromProps,
  DispatchFromProps,
  any,
  HandymanAppState
>(mapStateToProps, {
  logout,
  getCurrentUserDetails
})(AppBarComponent);